import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { SearchInput } from '../input/input';

interface ContainerProps {
    columns: string[];
    children?: React.ReactNode;
}

const Container = styled.div<ContainerProps>`
    display: grid;
    grid-template-columns: ${(props) => props.columns.join(' ')};
    grid-auto-rows: minmax(40px, auto);
`;

const Header = styled.div`
    display: contents;
    cursor: pointer;

    > * {
        &:first-child {
            padding-left: 10px;
        }
    }
    &:last-child {
        align-items: flex-end;
    }
`;

const HeaderCell = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 12px 10px;
    border-bottom: solid 1px #dbe2e6;
    font-weight: bold;
    font-size: 14px;

    .compact-table & {
        padding: 8px 10px;
    }
`;

export interface StyledRowProps {
    selected?: boolean;
    disabled?: boolean;
    className?: string;
}

const StyledRow = styled.div<StyledRowProps & { $clickable?: boolean }>`
    display: contents;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : props.$clickable ? 'pointer' : 'default')};
    ${(props) =>
        props.selected &&
        css`
            & > * {
                background-color: #f6f8f9;
            }
        `}

    ${(props) =>
        props.disabled &&
        css`
            & > * {
                opacity: 0.5;
                pointer-events: none;
            }
        `}

    &:hover > * {
        background-color: #f6f8f9;
    }

    > * {
        &:first-child {
            padding-left: 10px;
        }
        border-bottom: solid 1px #dbe2e6;
    }
`;

export interface RowProps {
    cells: (React.ReactNode | [React.ReactNode, { isInput?: boolean }])[];
    onEnter?: () => void;
    onLeave?: () => void;
    onClick?: () => void;
}

const getCellComponent = (cell: React.ReactNode | [React.ReactNode, { isInput?: boolean }]) => {
    if (Array.isArray(cell)) return cell[0];
    return cell;
};

const getCellProperties = (cell: React.ReactNode | [React.ReactNode, { isInput?: boolean }]) => {
    if (Array.isArray(cell)) return cell[1];
    return { isInput: false };
};

export const Row: FC<RowProps & StyledRowProps> = ({ onClick, onEnter, onLeave, cells, className, disabled }) => {
    return (
        <StyledRow
            $clickable={!!onClick}
            onClick={disabled ? undefined : onClick}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            className={className}
            disabled={disabled}
        >
            {cells.map((cell, i) => (
                <Cell $isInput={getCellProperties(cell).isInput} key={i}>
                    {getCellComponent(cell)}
                </Cell>
            ))}
        </StyledRow>
    );
};

export const Cell = styled.span<{ $isInput?: boolean; $isPrintable?: boolean; $isHidden?: boolean }>`
    display: inline-flex;
    align-items: center;
    padding: ${(props) => (props.$isInput ? '12px 15px 12px 0px' : '12px 10px')};

    .compact-table & {
        padding: ${(props) => (props.$isInput ? '6px 15px 6px 0px' : '6px 10px')};
        min-height: 70px;
        box-sizing: border-box;
    }
`;

export const ActionsCell = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

export const LinkCell = styled.div`
    width: 100%;

    a {
        font-weight: 600;
        color: ${(props) => props.theme.app.primary};
        text-decoration: none;
        vertical-align: middle;

        &:hover {
            text-decoration: underline;
            cursor: pointer;
        }
    }
`;

const normalizeHeading = (heading: TableHeading): NormalizedTableHeading => {
    if (Array.isArray(heading)) {
        return heading;
    } else {
        return [heading, '1fr'];
    }
};

const headingSize = (heading: NormalizedTableHeading) => heading[1];
const headingName = (heading: NormalizedTableHeading) => heading[0];
type NormalizedTableHeading = [string | React.ReactElement, string];
export type TableHeading = string | NormalizedTableHeading;

interface TableProps {
    headings: TableHeading[];
    children?: React.ReactNode;
    isPrintable?: boolean;
    compact?: boolean;
    'data-testid'?: string;
}

export const Table: FC<TableProps> = ({ headings, children, compact, ...rest }) => {
    const normalizedHeadings = headings.map(normalizeHeading);

    return (
        <Container columns={normalizedHeadings.map(headingSize)} className={compact ? 'compact-table' : undefined} {...rest}>
            <Header>
                {normalizedHeadings.map(headingName).map((name, i) => (
                    <HeaderCell key={i}>{name}</HeaderCell>
                ))}
            </Header>

            {children}
        </Container>
    );
};

const TableFilter = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 15px;
    margin: 20px 0 20px 0;
`;

const LeftTableFilter = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    grid-gap: 15px;
    flex: 1;
`;

const SearchInputContainer = styled.div`
    flex: 1;

    & > * {
        width: 100%;
        input {
            width: 100%;
        }
    }
`;

interface TableFilterHeaderProps {
    className?: string;
    showSearch?: boolean;
    onSearchChange?: (filter: string) => void;
    placeholder?: string;
    value?: string;
    autoFocus?: boolean;
    children?: React.ReactNode;
    customLeftComponent?: React.ReactNode;
    customRightComponent?: React.ReactNode;
}

export const TableFilterHeader: FC<TableFilterHeaderProps> = ({
    className,
    showSearch,
    onSearchChange,
    placeholder,
    value,
    autoFocus,
    children,
    customLeftComponent,
    customRightComponent
}) => {
    return (
        <TableFilter className={className}>
            {customLeftComponent}
            {children && <LeftTableFilter>{children}</LeftTableFilter>}
            {showSearch && (
                <SearchInputContainer className='search-input-container'>
                    <SearchInput autoFocus={autoFocus} placeholder={placeholder} value={value} onChange={(e) => onSearchChange!(e.currentTarget.value)} />
                </SearchInputContainer>
            )}
            {customRightComponent}
        </TableFilter>
    );
};
